"use client";

import type { ComponentType } from "react";
import type { LeadFlowStepItem } from "../types";

import {
  AnswerSummaryItem,
  ContentGridItem,
  IconItem,
  PhoneCtaItem,
  ReviewStarsItem,
  RichTextItem,
  TestimonialItem,
} from "./items";
import {
  CareTypeQuestion,
  ContactQuestion,
  PostalCodeQuestion,
  SingleChoiceQuestion,
} from "./questions";

type LeadFlowItemRendererProps = {
  item: LeadFlowStepItem;
};

type LeadFlowItemRenderer = ComponentType<LeadFlowItemRendererProps>;

type LeadFlowItemRenderers = {
  [ItemType in LeadFlowStepItem["_type"]]?: ComponentType<{
    item: Extract<LeadFlowStepItem, { _type: ItemType }>;
  }>;
};

function LeadFlowItemPlaceholder(_props: LeadFlowItemRendererProps) {
  return null;
}

function LeadFlowCareForQuestion({
  item,
}: {
  item: Extract<LeadFlowStepItem, { _type: "leadFlowCareForQuestion" }>;
}) {
  return <SingleChoiceQuestion fieldName="careFor" item={item} />;
}

function LeadFlowUrgencyQuestion({
  item,
}: {
  item: Extract<LeadFlowStepItem, { _type: "leadFlowUrgencyQuestion" }>;
}) {
  return <SingleChoiceQuestion fieldName="urgency" item={item} />;
}

const LEAD_FLOW_ITEM_RENDERERS: LeadFlowItemRenderers = {
  leadFlowAnswerSummary: AnswerSummaryItem,
  leadFlowCareForQuestion: LeadFlowCareForQuestion,
  leadFlowCareTypeQuestion: CareTypeQuestion,
  leadFlowContactQuestion: ContactQuestion,
  leadFlowContentGrid: ContentGridItem,
  leadFlowIconItem: IconItem,
  leadFlowPhoneCta: PhoneCtaItem,
  leadFlowPostalCodeQuestion: PostalCodeQuestion,
  leadFlowReviewStars: ReviewStarsItem,
  leadFlowRichText: RichTextItem,
  leadFlowUrgencyQuestion: LeadFlowUrgencyQuestion,
  testimonialItem: TestimonialItem,
};

function getLeadFlowItemRenderer(item: LeadFlowStepItem): LeadFlowItemRenderer {
  // The mapped type pairs each renderer with its own item type, which the
  // index lookup can't narrow back from the union.
  const renderer = LEAD_FLOW_ITEM_RENDERERS[item._type] as
    | LeadFlowItemRenderer
    | undefined;

  return renderer ?? LeadFlowItemPlaceholder;
}

type StepItemsProps = {
  items: LeadFlowStepItem[];
};

/** Renders step, info panel, interstitial, and thank-you items in authored order. */
function StepItems({ items }: StepItemsProps) {
  return items.map((item) => {
    const Renderer = getLeadFlowItemRenderer(item);

    return <Renderer item={item} key={item._key} />;
  });
}

export { StepItems };
